import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import ProductGrid from "./ProductGrid";
import LogoGrid from "./LogoGrid.tsx";
import SEOHead from "../../components/SEOHead";


const Home: React.FC = () => {
  const navigate = useNavigate();

  const testimonials = [
    { name: "Raosaheb Panmand", text: "The HIRA workshop was insightful and expanded my awareness about hazard identification.", role: "Safety Officer" },
    { name: "Harale Pragati", text: "It was a well-structured HIRA session with clear explanations and practical relevance.", role: "Site Engineer" },
    { name: "Gawali Radha", text: "Great learning experience from the HIRA training, especially the real-world examples shared.", role: "Operations" },
    { name: "S.K Kande", text: "The HIRA content was highly informative and boosted my confidence in handling risks.", role: "Manager" },
    { name: "Ganesh Dali", text: "The HIRA workshop was interactive and engaging – complex concepts were simplified.", role: "Supervisor" },
    { name: "Mubarak Sabkhan", text: "A valuable HIRA session that helped me recognize hidden workplace hazards.", role: "Safety Lead" },
    { name: "Mrinal Das", text: "Very useful HIRA training – it added depth to my knowledge of safety protocols.", role: "Engineer" },
    { name: "Rakesh Meshram", text: "Clear explanations with relatable examples made the HIRA session excellent.", role: "Participant" },
    { name: "Vijay Dandamani", text: "I appreciated the focus on practical application during the HIRA workshop.", role: "Coordinator" },
    { name: "Sachin Shirke", text: "The HIRA session helped me improve my hazard identification and response approach.", role: "Team Lead" },
  ];

  // Duplicate testimonials for seamless scrolling loop
  const scrollTestimonials = [...testimonials, ...testimonials];

  const testimonialScrollRef = useRef<HTMLDivElement>(null);
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);

  useEffect(() => {
    const scrollContainer = testimonialScrollRef.current;
    if (!scrollContainer) return;

    const speed = 1; // Pixels per frame
    let animationId: number;

    const step = () => {
      // Disable auto-scroll logic on mobile devices (< 768px) to prevent fighting with native swipe/snap
      if (!isTestimonialPaused && window.innerWidth >= 768) {
        scrollContainer.scrollLeft += speed;

        // Check if we reached the end
        if (scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth - 1) {
          scrollContainer.scrollLeft = 0; // Snap back to start
        }
      }
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationId);
  }, [isTestimonialPaused]);

  return (
    <div className="home-container">
      <SEOHead
        title="WeCare EHS | Safety Training, Audits & EHS Consultancy India"
        description="WeCare EHS — India's trusted provider of workplace safety training, ISO 9001/14001/45001 certification, HIRA, EHS consultancy, and compliance services. 1000+ professionals trained."
        keywords="EHS training India, workplace safety, ISO 9001, ISO 14001, ISO 45001, HIRA, safety consultancy, fire safety training, WeCare EHS"
        canonical="/"
      />
      {/* Hero Section */}

      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-badge">
            🚀 Elevate Your Safety Standards
          </div>
          <h1 className="hero-title">
            <span className="text-gradient">Through Safety Excellence</span>
            <br />
            Empowering Industries
          </h1>
          <p className="hero-subtitle">
            We provide world-class EHS consultancy and safety training programs designed to
            protect your workforce and ensure compliance. Join the leaders in industrial safety.
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => navigate("/training")}>
              View Training Programs
            </button>
            <button className="btn-secondary" onClick={() => navigate("/contact")}>
              Contact Us
            </button>
          </div>

          {/* Stats / Trust Indicators */}
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-value">50+</span>
              <span className="stat-label">Safety Audits</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-value">1000+</span>
              <span className="stat-label">Trained Professionals</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-value">100%</span>
              <span className="stat-label">Compliance Success</span>
            </div>
          </div>
        </div>

      </section>

      {/* Product Grid Section - Logic Preserved */}
      {/* We wrap it to ensure it blends, but ProductGrid itself is untouched */}
      <section className="product-section-wrapper">
        <div className="section-header-center">
          <h2 className="section-heading">Our Core Services</h2>
          <p className="section-subheading">Explore our specialized offerings tailored for your industry.</p>
        </div>
        <ProductGrid />
      </section>

      {/* Marquee Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-header-center">
          <h2 className="section-heading">Trusted by Experts</h2>
          <p className="section-subheading">Hear from the professionals who have experienced our training.</p>
        </div>

        <div className="testimonial-scroll-wrapper">
          <div
            className="testimonial-scroll-container"
            ref={testimonialScrollRef}
            onMouseEnter={() => setIsTestimonialPaused(true)}
            onMouseLeave={() => setIsTestimonialPaused(false)}
            onTouchStart={() => setIsTestimonialPaused(true)}
            onTouchEnd={() => setIsTestimonialPaused(false)}
          >
            {scrollTestimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="quote-icon">❝</div>
                <p className="testimonial-text">{testimonial.text}</p>
                <div className="testimonial-profile">
                  <div className="avatar-placeholder">
                    {testimonial.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="profile-info">
                    <h4 className="profile-name">{testimonial.name}</h4>
                    <span className="profile-role">{testimonial.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Grid Section */}
      <section className="logo-section">
        <h2 className="section-heading-small">Companies We Work With</h2>
        <div className="logo-grid-wrapper">
          <LogoGrid />
        </div>
      </section>

      {/* Modern CTA Section */}
      <section className="cta-section">
        <div className="cta-background-shape"></div>
        <div className="cta-container">
          <h2 className="cta-heading">Ready to Transform Your Workplace Safety?</h2>
          <p className="cta-description">
            Don't compromise on safety. Get certified training and expert consultancy today.
          </p>
          <button
            className="btn-cta"
            onClick={() => navigate("/register")}
          >
            Get Started Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;
